import { Component, DestroyRef, inject } from '@angular/core';
import { DirectivesModule } from '../../../../../../shared/directives/directives.module';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { UiButtonComponent } from '@ui';
import { PersonDetailsFormComponent } from '../../components/person-details-form/person-details-form.component';
import { PersonService } from '../../../../../../services/person.service';
import { PersonModel } from '@models/person.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith, take } from 'rxjs';
import { PersonDialogService } from '../../../../../../services/person-dialog.service';
import { PersonGroupsModel } from '@models/person-groups.model';
import { Store } from '@ngrx/store';
import { personFeature } from '@states/person/person.feature';
import { Actions, ofType } from '@ngrx/effects';
import { PersonActions } from '@states/person/person.actions';

@Component({
  selector: 'app-save-new-person-dialog',
  imports: [
    DirectivesModule,
    ReactiveFormsModule,
    TranslatePipe,
    UiButtonComponent,
    PersonDetailsFormComponent,
  ],
  templateUrl: './save-new-person-dialog.component.html',
  styleUrl: './save-new-person-dialog.component.scss',
})
export class SaveNewPersonDialogComponent {
  private readonly personService = inject(PersonService);
  private readonly personDialogService = inject(PersonDialogService);
  private readonly dialogRef = inject(MatDialogRef<SaveNewPersonDialogComponent>);
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);

  private readonly data = inject<PersonModel.UnsavedPerson | PersonModel.SaveNewPersonDialogData>(MAT_DIALOG_DATA);
  protected readonly person = 'person' in this.data ? this.data.person : this.data;
  protected readonly recognitionResult = 'person' in this.data ? this.data.recognitionResult : undefined;
  protected readonly personNameForm = new FormControl<string>(PersonModel.getDefaultPersonName(1), {
    nonNullable: true,
    validators: [Validators.required],
  });
  protected readonly unsavedPersonsToken = this.store$.selectSignal(personFeature.selectUnsavedPersonsToken);
  protected readonly isCreating = this.store$.selectSignal(personFeature.selectIsCreatingPersonFromImage);
  protected readonly isPersonNameValid = toSignal(
    this.personNameForm.valueChanges.pipe(
      startWith(this.personNameForm.value),
      map((value) => !!value.trim()),
    ),
    { initialValue: false },
  );

  protected getImagePath(person: PersonModel.UnsavedPerson, gcp: boolean = true): string {
    return this.personService.getPersonImagePath(person.bestImage, false, gcp);
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected openAddPersonToGroupsDialog(): void {
    if (!this.isPersonNameValid()) {
      return;
    }

    const unsavedPerson = {
      bucketId: this.person.bucketId,
      name: this.personNameForm.value.trim(),
    };

    const data: PersonGroupsModel.AddPersonToGroupsDialogData = {
      personType: PersonGroupsModel.AddPersonToGroupDialogTab.UnsavedPersons,
      unsavedPersonDtos: [unsavedPerson],
      personIds: [],
    };

    this.personDialogService
      .openAddPersonToGroups(data)
      .afterClosed()
      .pipe(
        filter(Boolean),
        take(1),
      )
      .subscribe(() => this.closeDialog());
  }

  protected savePerson(): void {
    const name = this.personNameForm.value.trim();
    const token = this.unsavedPersonsToken();
    const positiveTrackers = this.recognitionResult?.positiveTrackerIds ?? [];

    if (!name || !token || this.isCreating() || !positiveTrackers.length) {
      return;
    }

    const request: PersonModel.API.CreatePersonV2Request = {
      name,
      token,
      bucketId: this.person.bucketId,
      bestImage: this.person.bestImage,
      positiveTrackers,
      negativeTrackers: this.recognitionResult?.negativeTrackerIds ?? [],
    };

    this.closeDialogOnCreateSuccess();
    this.store$.dispatch(PersonActions.createPerson({ request }));
  }

  private closeDialogOnCreateSuccess(): void {
    this.actions$
      .pipe(
        ofType(PersonActions.createPersonSuccess),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.closeDialog());
  }
}
